import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  WishListMiniPopupEventIDs,
  WishListMiniPopupMessageTypes,
} from '@/domains/productDiscovery/HeaderLeonidas/components/WishListIconWithMiniPopup/constants';
import {
  hideWishlistPopup,
  showWishlistPopup,
} from '@/domains/productDiscovery/HeaderLeonidas/HeaderLeonidas.actions';
import { WishListMiniPopupState } from '@/domains/productDiscovery/HeaderLeonidas/interfaces/HeaderLeonidas.model';
import { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';

import {
  addedToWishlistWithName,
  addToWishlistMiniPopupMessage,
  addToWistListActionText,
  deletedFromWishlistMiniPopupMessage,
} from '../components/main/translations';
import { interactiveWishlistMiniPopupEvent } from './../../../productDiscovery/HeaderLeonidas/HeaderLeonidas.actions';

const POPUP_TIMEOUT = 6000;

export const useHeaderWishlistMiniPopup = (
  onMiniPopupActionClick: () => void,
) => {
  const dispatch = useDispatch();
  const popupEffectEventID = useSelector(wishlistMiniPopupEventSelector);
  const [curTimeoutTimer, setCurTimeoutTimer] = useState<NodeJS.Timeout | null>(
    null,
  );

  const handleMiniPopupEffectEvent = useCallback(() => {
    switch (popupEffectEventID) {
      case WishListMiniPopupEventIDs.CLICK_ACTION:
        dispatch(hideWishlistPopup());
        onMiniPopupActionClick();
        break;
      case WishListMiniPopupEventIDs.CLICK_CLOSE:
      case WishListMiniPopupEventIDs.CLICK_ELSEWHERE:
        dispatch(hideWishlistPopup());
        break;
    }
  }, [popupEffectEventID, onMiniPopupActionClick, dispatch]);

  useEffect(() => {
    handleMiniPopupEffectEvent();
    return () => {
      dispatch(
        interactiveWishlistMiniPopupEvent(WishListMiniPopupEventIDs.CONSUMED),
      );
    };
  }, [handleMiniPopupEffectEvent, popupEffectEventID, dispatch]);

  const hideMiniPopupLater = useCallback(() => {
    if (curTimeoutTimer) clearTimeout(curTimeoutTimer);
    const timerInst = setTimeout(() => {
      dispatch(hideWishlistPopup());
      setCurTimeoutTimer(null);
    }, POPUP_TIMEOUT);
    setCurTimeoutTimer(timerInst);
  }, [dispatch, curTimeoutTimer]);

  const actionShowMiniPopupSuccess = useCallback(
    (wishListName: string, imageUrlOfProduct: string) => {
      const wishlistMiniConfig: WishListMiniPopupState = {
        imageUrl: imageUrlOfProduct || '',
        message: String(addedToWishlistWithName.format({ wishListName })),
        messageType: WishListMiniPopupMessageTypes.NORMAL,
      };
      dispatch(showWishlistPopup(wishlistMiniConfig));
      hideMiniPopupLater();
    },
    [dispatch, hideMiniPopupLater],
  );

  const actionShowMiniPopupFailure = useCallback(
    (msg: string) => {
      const wishlistMiniConfig: WishListMiniPopupState = {
        message: msg,
        messageType: WishListMiniPopupMessageTypes.ALERT,
      };
      dispatch(showWishlistPopup(wishlistMiniConfig));
      hideMiniPopupLater();
    },
    [dispatch, hideMiniPopupLater],
  );

  const actionShowMiniPopupDeleted = useCallback(
    (imageUrlOfProduct: string) => {
      const wishlistMiniConfig: WishListMiniPopupState = {
        imageUrl: imageUrlOfProduct || '',
        message: deletedFromWishlistMiniPopupMessage,
        messageType: WishListMiniPopupMessageTypes.NORMAL,
      };
      dispatch(showWishlistPopup(wishlistMiniConfig));
      hideMiniPopupLater();
    },
    [dispatch, hideMiniPopupLater],
  );

  const actionShowMiniPopupWithAction = useCallback(
    (imageUrl: string) => {
      const wishlistMiniConfig: WishListMiniPopupState = {
        imageUrl,
        message: addToWishlistMiniPopupMessage,
        actionText: addToWistListActionText,
        messageType: WishListMiniPopupMessageTypes.NORMAL,
      };
      dispatch(showWishlistPopup(wishlistMiniConfig));
      hideMiniPopupLater();
    },
    [dispatch, hideMiniPopupLater],
  );

  return {
    actionShowMiniPopupSuccess,
    actionShowMiniPopupFailure,
    actionShowMiniPopupWithAction,
    actionShowMiniPopupDeleted,
  };
};

function wishlistMiniPopupEventSelector(
  state: Partial<SpartacuxProductDiscoveryDomainState>,
): WishListMiniPopupEventIDs | undefined {
  return state.productDiscovery?.headerLeonidas?.ui?.wishlistMiniPopup
    ?.effectEventId;
}
